// RollupModal.jsx

import React, { useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Hooks
import useRollupEditor from './hooks/useRollupEditor';

// CSS
import './RollupModal.css';

// Components
import RollupEditorForm from './RollupEditorForm';
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import { toast } from 'react-toastify';
import {
  normalizeRollupDefinition,
  normalizeRollupValueColor,
} from 'rollups/rollupDefinitions';
import { validateStudioRollupEditorDraft } from 'studio/rollups/studioRollupDraft';

/**
 * RollupModal
 *
 * Renders the create/edit modal for one saved rollup definition.
 *
 * @returns {ReactNode} The rendered component.
 */
const RollupModal = React.memo(({
  fields = [],
  isOpen,
  mode = 'create',
  onDelete,
  onSave,
  rollup = null,
  setOpen,
  typeKey = null,
}) => {
  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- STATE ----- //

  const [error, setError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ----- HOOKS ----- //

  const {
    draft,
    editorProps,
    fieldOptions,
  } = useRollupEditor({
    fields,
    isOpen,
    rollup,
    typeKey,
  });

  // ----- HANDLERS ----- //

  /**
 * Handles save.
 */

  const handleSave = async () => {
    if (isSaving || isDeleting) return;

    const validation = validateStudioRollupEditorDraft(draft, {
      fields,
      typeKey,
    });

    if (!validation.isValid) {
      setError(validation.error || 'Fix rollup validation errors before saving.');
      return;
    }

    setError('');
    setIsSaving(true);

    try {
      const nextDraft = normalizeRollupDefinition({
        ...validation.payload,
        valueColor: normalizeRollupValueColor(draft?.valueColor),
      }, { typeKey });

      const didSave = await onSave?.(nextDraft);
      if (didSave === false) return;
      setOpen(false);
    } catch (saveError) {
      toast.error(saveError?.message || 'Could not save rollup.');
    } finally {
      setIsSaving(false);
    }
  };

  /**
 * Handles delete.
 */
  const handleDelete = async () => {
    if (mode !== 'edit' || !draft?.key || isDeleting || isSaving) return;

    setIsDeleting(true);
    setError('');

    try {
      const didDelete = await onDelete?.(draft.key);
      if (didDelete === false) return;
      setOpen(false);
    } catch (deleteError) {
      toast.error(deleteError?.message || 'Could not delete rollup.');
    } finally {
      setIsDeleting(false);
    }
  };
  // ----- RENDER ----- //

  return (
    <Modal
      iconKey="query_stats"
      isOpen={isOpen}
      maxHeight={760}
      title={mode === 'edit' ? 'Edit Rollup' : 'New Rollup'}
      width={isMobile ? '100%' : 480}
      onClose={() => setOpen(false)}
    >
      <ModalForm>
        <RollupEditorForm
          {...editorProps}
          error={error}
        />
      </ModalForm>

      {/* ACTIONS */}
      <ModalButtons>
        <ModalButtonSection align="START">
          {mode === 'edit' ? (
            <Button
              enabled={!isDeleting && !isSaving}
              iconKey="delete"
              label={isDeleting ? 'Deleting...' : 'Delete'}
              onClick={handleDelete}
            />
          ) : null}
        </ModalButtonSection>

        <ModalButtonSection align="END">
          <Button
            enabled={!isDeleting && !isSaving}
            iconKey="close"
            label="Cancel"
            onClick={() => setOpen(false)}
          />
          <Button
            enabled={!isDeleting && !isSaving && fieldOptions.length > 0}
            iconKey="check"
            label={isSaving ? 'Saving...' : 'Save'}
            onClick={handleSave}
          />
        </ModalButtonSection>
      </ModalButtons>
    </Modal>
  );
});

// Dev Tools
RollupModal.displayName = 'RollupModal';

export default RollupModal;
